import { useEffect, useMemo, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogCloseButton,
} from '@wartek-id/dialog-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Text } from '@wartek-id/text'
import { useToast } from '@wartek-id/toast'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'

import type { SKPPeriodV2 } from 'types/kinerja'

type Props = Omit<DialogProps, 'children'>

export function PeriodDialog(props: Props) {
  const toast = useToast()

  const { periods, period, setPeriod } = usePeriodContext()
  const { isKsDefinitif, isKsPlt, isKepalaDinas, isPengawasSekolah } =
    useRoleContext()

  const [selectedPeriod, setSelectedPeriod] = useState<
    OptionItem & SKPPeriodV2
  >()

  useEffect(() => {
    if (period) {
      setSelectedPeriod({
        name: formatPeriod(period.startDate, period.endDate),
        disabled: period.isApproved,
        ...period,
      })
    }
  }, [period])

  const periodOptions = useMemo(() => {
    return (periods || []).map((item) => {
      let disabled = false

      if (isKsDefinitif || isKsPlt || isKepalaDinas || isPengawasSekolah) {
        disabled = false
      } else {
        disabled = !item.isApproved
      }

      return {
        name: formatPeriod(item.startDate, item.endDate),
        disabled: disabled,
        ...item,
      }
    })
  }, [periods, isKsDefinitif, isKsPlt, isKepalaDinas, isPengawasSekolah])

  return (
    <Dialog size="sm" {...props}>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader title="Ganti Periode Pengelolaan Kinerja" />

        <div className="p-6">
          <Text className="mb-2" color="subdued">
            Pilih Periode
          </Text>
          <div className="flex gap-4">
            <div className="flex-1">
              <CustomSelect
                placeholder="."
                options={periodOptions}
                onChange={(option: OptionItem & SKPPeriodV2) =>
                  setSelectedPeriod(option)
                }
                value={selectedPeriod}
                customList={(props) => {
                  const item = props.item as OptionItem & SKPPeriodV2

                  return !item.disabled ? (
                    <div className="py-3 px-6 -my-2 -ml-4 -mr-8 hover:bg-surface-hovered hover:text-default">
                      <Text className="text-base" color="inherit">
                        {item.name}
                      </Text>
                    </div>
                  ) : (
                    <div className="py-3 px-6 -my-2 -ml-4 -mr-8 flex flex-col gap-1 bg-surface-disabled">
                      <Text className="text-base" color="disabled">
                        {item.name}
                      </Text>
                      {isKepalaDinas || isPengawasSekolah ? (
                        <Text className="text-sm" color="disabled">
                          Belum masuk periode.
                        </Text>
                      ) : (
                        <Text className="text-sm" color="disabled">
                          Belum dimulai oleh atasan.
                        </Text>
                      )}
                    </div>
                  )
                }}
              />
            </div>
            <Button
              disabled={!selectedPeriod || period?.id === selectedPeriod.id}
              onClick={() => {
                toast({
                  message: `Berhasil ganti periode ke: ${selectedPeriod.name}`,
                })
                setPeriod(selectedPeriod)
                props.onClose()
              }}
            >
              Pilih
            </Button>
          </div>
        </div>
        <DialogCloseButton />
      </DialogContent>
    </Dialog>
  )
}
